import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AlertTriangle, Plus, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Safety", "Security", "Cleaning", "Lighting", "HVAC", "Music or Audio", "Wi-Fi or Network", "TV or Digital Display", "Hologram Fan", "Stock or Merchandising", "Maintenance", "Other"];
const URGENCIES = ["low", "medium", "high", "emergency"];
const STATUSES = ["open", "in_progress", "waiting_vendor", "resolved", "closed"];

export default function Issues() {
  const { user } = useAuth();
  const [issues, setIssues] = useState([]);
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    const params = filter ? { status_: filter } : {};
    api.get("/issues", { params }).then(({ data }) => setIssues(data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (i, status) => {
    try {
      await api.patch(`/issues/${i.id}`, { status });
      toast.success("Issue updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="issues-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Operations</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Issue Management</h1>
        </div>
        <button className="hos-btn-primary flex items-center gap-2" onClick={() => setCreating(true)} data-testid="new-issue-btn">
          <Plus size={16}/> Report Issue
        </button>
      </div>

      <div className="hos-card p-4 flex gap-3">
        <select className="hos-input max-w-[200px]" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="issues-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="grid gap-3">
        {issues.map((i) => (
          <div key={i.id} className="hos-card p-5 flex flex-col sm:flex-row gap-4">
            <div className={`w-1 rounded-full`} style={{ background: urgencyColor(i.urgency) }}/>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <div className="font-mono text-sm text-[color:var(--gold)]">{i.issue_number}</div>
                <span className={`status-pill ${statusPillCls(i.status)}`}>{i.status}</span>
                <span className="text-xs text-[color:var(--text-secondary)]">{i.category} · {i.urgency}</span>
              </div>
              <div className="mt-1">{i.description}</div>
              <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                By {i.reported_by_name} · {new Date(i.reported_at).toLocaleString()}
              </div>
              {i.action_taken && <div className="text-sm mt-1 text-[color:var(--text-secondary)]">Action: {i.action_taken}</div>}
            </div>
            {["admin", "manager"].includes(user.role) && (
              <select className="hos-input max-w-[180px]" value={i.status} onChange={(e) => changeStatus(i, e.target.value)} data-testid={`issue-status-${i.id}`}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            )}
          </div>
        ))}
        {issues.length === 0 && <div className="hos-card p-8 text-center text-[color:var(--text-secondary)]">No issues.</div>}
      </div>

      {creating && <CreateIssue onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function urgencyColor(u) {
  return u === "emergency" ? "#E63946" : u === "high" ? "#F4A261" : u === "medium" ? "#D4AF37" : "#457B9D";
}
function statusPillCls(s) {
  if (s === "resolved" || s === "closed") return "status-open";
  if (s === "in_progress" || s === "waiting_vendor") return "status-progress";
  return "status-error";
}

function CreateIssue({ onClose, onCreated }) {
  const [f, setF] = useState({ category: CATEGORIES[0], description: "", urgency: "medium", action_taken: "", follow_up_required: false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.description.trim()) return toast.error("Description required");
    setBusy(true);
    try {
      const { data } = await api.post("/issues", f);
      toast.success(`Created ${data.issue_number}`);
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="hos-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl">Report Issue</h3>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="grid gap-3">
          <select className="hos-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="new-issue-category">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea className="hos-input min-h-[80px]" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="new-issue-description"/>
          <select className="hos-input" value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} data-testid="new-issue-urgency">
            {URGENCIES.map((u) => <option key={u} value={u}>{u.toUpperCase()}</option>)}
          </select>
          <input className="hos-input" placeholder="Action taken" value={f.action_taken} onChange={(e) => setF({ ...f, action_taken: e.target.value })}/>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button className="hos-btn-outline" onClick={onClose}>Cancel</button>
          <button className="hos-btn-primary" onClick={submit} disabled={busy} data-testid="new-issue-submit">Create</button>
        </div>
      </div>
    </div>
  );
}
